import React from "react";

import { Box, Chip, Stack, Typography, useTheme } from "@mui/material";

import type {
  ManifestData,
  ManifestPreviewResponse,
} from "./manifest-preview-constants";

interface ManifestServicesSummaryProps {
  manifest: ManifestData;
  resolved: ManifestPreviewResponse["resolved"] | null;
}

export function ManifestServicesSummary({
  manifest,
  resolved,
}: ManifestServicesSummaryProps): React.ReactElement {
  const theme = useTheme();

  return (
    <>
      {/* Resolved services summary */}
      {resolved && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Resolved Services ({resolved.environment})
          </Typography>
          <Stack spacing={0.5}>
            {resolved.services.map((svc) => (
              <Box
                key={svc.name}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ minWidth: 80 }}
                >
                  {svc.name}
                </Typography>
                <Chip label={svc.type} size="small" variant="outlined" />
                {svc.internetFacing && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.success.main, fontWeight: 600 }}
                  >
                    public
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: "auto" }}
                >
                  {svc.replicas} replica{svc.replicas !== 1 ? "s" : ""} ·{" "}
                  {svc.resources.cpu} / {svc.resources.memory}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Raw manifest services (when no resolved data) */}
      {!resolved && manifest && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Services Summary
          </Typography>
          <Stack spacing={0.5}>
            {manifest.spec.services.map((svc) => (
              <Box
                key={svc.name}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ minWidth: 80 }}
                >
                  {svc.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {svc.type}
                </Typography>
                {svc.port && (
                  <Typography variant="caption" color="text.secondary">
                    :{svc.port}
                  </Typography>
                )}
                {svc.internetFacing && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.success.main, fontWeight: 600 }}
                  >
                    public
                  </Typography>
                )}
                {svc.resources && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: "auto" }}
                  >
                    {svc.resources.cpu ?? "-"} / {svc.resources.memory ?? "-"}
                    {svc.storage ? ` / ${svc.storage}` : ""}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Info about deploy mode */}
      {manifest.spec.deployMode && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Deploy Mode
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {manifest.spec.deployMode === "rusha_managed" &&
              "Rusha builds and deploys your stack automatically (GitOps for K8s)."}
            {manifest.spec.deployMode === "ci_build_rusha_deploy" &&
              "CI builds images, then Rusha deploys via GitOps."}
            {manifest.spec.deployMode === "rusha_build_ci_deploy" &&
              "Rusha builds, CI deploys (K8s GitOps or cloud-native)."}
            {manifest.spec.deployMode === "ci_managed" &&
              "CI controls the full build and deploy pipeline."}
          </Typography>
        </Box>
      )}
    </>
  );
}

export default ManifestServicesSummary;
